/*
 * business-card/vcard.js — the QR that is worth the 15 mm it occupies.
 *
 * WHAT EVERY OTHER CARD MAKER PUTS IN THE QR
 * ──────────────────────────────────────────
 * A link to a website. Which the card already prints in readable text, so the
 * QR duplicates the one field a human can retype in four seconds, and does not
 * carry the one they cannot: the phone number, into the address book, without
 * typing. A vCard QR turns "I'll add you later" into a two-second scan, and
 * "later" is where paper business cards go to die.
 *
 * THE CONSTRAINT NOBODY MODELS: A QR HAS A PHYSICAL MODULE SIZE
 * ────────────────────────────────────────────────────────────
 * A QR is a grid of modules, and the printed size of ONE MODULE decides whether
 * a phone can read it. More data means more modules in the same square, so a
 * QR that scans perfectly on screen can be unscannable at 15 mm — and it looks
 * completely fine in the preview, because on a 96 dpi monitor at 400 % zoom
 * every module is enormous.
 *
 * The camera side of it: a phone needs roughly 2-3 sensor pixels per module to
 * decode reliably, which at typical hand-held distance works out around
 * 0.4 mm/module. The print side: at 300 dpi one module should be a whole number
 * of dots, and below ~0.25 mm the ink spread alone closes the gaps.
 *
 * So this module reports the module size in millimetres, and the UI refuses to
 * pretend. That is a measurement, not an opinion, and it is the difference
 * between a card that works and a card with a decorative square on it.
 *
 * ⚠️ CYRILLIC DOUBLES THE PAYLOAD. QR byte mode is bytes, and UTF-8 spends two
 * of them on every Russian letter. "Иван Петров" is 21 bytes, "Ivan Petrov" is
 * 11. A Russian vCard therefore lands two or three QR versions higher than the
 * identical English one, which is exactly the audience most likely to hit the
 * unscannable wall — and exactly why the shortening ladder below exists.
 *
 * Pure module: no DOM, no browser API.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardVcard = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── escaping ───────────────────────────────────────────────────────────
     * RFC 6350 §3.4: backslash, comma, semicolon and newline are structural in
     * a vCard value and must be escaped. Skipping this is not cosmetic — an
     * unescaped semicolon in a company name ("ООО Ромашка; филиал") silently
     * splits the field, and the contact imports with a truncated employer.
     */
    function esc(v) {
        return String(v == null ? '' : v)
            .replace(/\\/g, '\\\\')
            .replace(/\n/g, '\\n')
            .replace(/,/g, '\\,')
            .replace(/;/g, '\\;');
    }

    /* Phone numbers go into TEL as the user typed them, but the value a scanner
     * DIALS should be unambiguous. Keeping the pretty form for display and a
     * normalised form for the URI is the only way to have both. */
    function telUri(v) {
        var s = String(v || '').trim();
        var plus = s.charAt(0) === '+';
        var digits = s.replace(/\D+/g, '');
        return digits ? ((plus ? '+' : '') + digits) : '';
    }

    function utf8Bytes(s) {
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
        return unescape(encodeURIComponent(s)).length;
    }

    /* Split a full name into the five vCard N components. Getting this wrong is
     * why so many scanned contacts land as one blob in the "first name" field
     * with an empty surname, which then sorts under И instead of П.
     *
     * Heuristic and honest about it: RU/EU order is "Имя Фамилия" and the
     * Russian patronymic makes three tokens common ("Иван Иванович Петров" is
     * rare; "Петров Иван Иванович" is the official order and "Иван Петров" the
     * everyday one). Two tokens → given + family. Three → treat the MIDDLE as
     * the additional name only when it looks like a patronymic, else the last
     * token is the family name. */
    var PATRONYMIC = /(ович|евич|ьич|инич|овна|евна|ична|инична)$/i;
    function splitName(full) {
        var parts = String(full || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return { family: '', given: '', additional: '' };
        if (parts.length === 1) return { family: '', given: parts[0], additional: '' };
        if (parts.length === 2) return { family: parts[1], given: parts[0], additional: '' };
        if (PATRONYMIC.test(parts[1])) {
            return { family: parts.slice(2).join(' '), given: parts[0], additional: parts[1] };
        }
        if (PATRONYMIC.test(parts[2])) {
            /* "Петров Иван Иванович" — the official RU order, family first. */
            return { family: parts[0], given: parts[1], additional: parts[2] };
        }
        return { family: parts[parts.length - 1], given: parts[0], additional: parts.slice(1, -1).join(' ') };
    }

    /* ── vCard 3.0 ──────────────────────────────────────────────────────────
     * 3.0 and not 4.0 deliberately. 4.0 is the better specification and 3.0 is
     * the one every phone camera, every scanner app and every address book
     * imports without argument. For a QR on a piece of card, compatibility is
     * the entire feature.
     */
    function vcard3(d, opt) {
        opt = opt || {};
        var n = splitName(d.name);
        var L = ['BEGIN:VCARD', 'VERSION:3.0'];
        L.push('N:' + esc(n.family) + ';' + esc(n.given) + ';' + esc(n.additional) + ';;');
        if (d.name) L.push('FN:' + esc(d.name));
        if (d.company || d.department) {
            L.push('ORG:' + esc(d.company || '') + (d.department ? ';' + esc(d.department) : ''));
        }
        if (d.role) L.push('TITLE:' + esc(d.role));
        if (d.phone) L.push('TEL;TYPE=CELL:' + esc(telUri(d.phone)));
        if (!opt.short && d.phone2) L.push('TEL;TYPE=WORK:' + esc(telUri(d.phone2)));
        if (d.email) L.push('EMAIL;TYPE=INTERNET:' + esc(d.email));
        if (d.site) L.push('URL:' + esc(normUrl(d.site)));
        if (!opt.short && d.address) {
            /* ADR is seven semicolon-separated components; putting the whole
             * address in the street slot is what everyone does and what every
             * address book displays correctly. */
            L.push('ADR;TYPE=WORK:;;' + esc(d.address) + ';;;;');
        }
        if (!opt.short && d.telegram) L.push('X-SOCIALPROFILE;TYPE=telegram:' + esc(tgUrl(d.telegram)));
        if (!opt.short && d.note) L.push('NOTE:' + esc(d.note));
        L.push('END:VCARD');
        return L.join('\r\n');
    }

    /* ── MECARD ─────────────────────────────────────────────────────────────
     * A far more compact contact format, understood natively by Android and by
     * the common iOS scanner apps. Roughly 40 % smaller than the same vCard,
     * which is one to two QR versions — often the whole difference between a
     * 0.33 mm module and a 0.45 mm one. Offered as the shortening step rather
     * than the default because vCard imports on more devices.
     */
    function mecard(d) {
        var n = splitName(d.name);
        var P = [];
        /* ⚠️ The comma between surname and given name is MECARD's own field
         * SEPARATOR, so it must survive unescaped. Building the string first and
         * escaping the whole thing — the obvious order — turns it into a literal
         * comma, and the contact imports as one blob in the surname field with a
         * comma in the middle of it. Caught by decoding an exported PDF, which
         * is the only place the mistake is visible: the QR renders and scans
         * perfectly either way. */
        var given = (n.given + (n.additional ? ' ' + n.additional : '')).trim();
        P.push('N:' + (n.family ? mesc(n.family) + ',' : '') + mesc(given));
        if (d.phone) P.push('TEL:' + mesc(telUri(d.phone)));
        if (d.email) P.push('EMAIL:' + mesc(d.email));
        if (d.company) P.push('ORG:' + mesc(d.company));
        if (d.site) P.push('URL:' + mesc(normUrl(d.site)));
        return 'MECARD:' + P.join(';') + ';;';
    }
    function mesc(v) {
        return String(v == null ? '' : v).replace(/([\\;:,])/g, '\\$1');
    }

    function normUrl(v) {
        var s = String(v || '').trim();
        if (!s) return '';
        if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
        return 'https://' + s.replace(/^\/+/, '');
    }
    function tgUrl(v) {
        var s = String(v || '').trim().replace(/^@/, '');
        if (/^https?:/i.test(s)) return s;
        return 'https://t.me/' + s;
    }
    function waUrl(v) {
        var digits = String(v || '').replace(/\D+/g, '');
        return digits ? 'https://wa.me/' + digits : '';
    }

    /* ── QR capacity ────────────────────────────────────────────────────────
     * Byte-mode capacity per version at each error-correction level. Only the
     * versions a card could plausibly need are tabulated; beyond version 20 the
     * module size is hopeless at card scale anyway and the answer is "shorten",
     * not "go bigger".
     *
     * Numbers are the ISO/IEC 18004 capacity table. They are what makes the
     * module-size prediction possible WITHOUT rendering the QR first, which is
     * what lets the UI warn while the user is still typing.
     */
    var CAP = {
        /* version: [L, M, Q, H] byte-mode capacity */
        1:  [17, 14, 11, 7],        2:  [32, 26, 20, 14],
        3:  [53, 42, 32, 24],       4:  [78, 62, 46, 34],
        5:  [106, 84, 60, 44],      6:  [134, 106, 74, 58],
        7:  [154, 122, 86, 64],     8:  [192, 152, 108, 84],
        9:  [230, 180, 130, 98],    10: [271, 213, 151, 119],
        11: [321, 251, 177, 137],   12: [367, 287, 203, 155],
        13: [425, 331, 241, 177],   14: [458, 362, 258, 194],
        15: [520, 412, 292, 220],   16: [586, 450, 322, 250],
        17: [644, 504, 364, 280],   18: [718, 560, 394, 310],
        19: [792, 624, 442, 338],   20: [858, 666, 482, 382],
    };
    var ECC_INDEX = { L: 0, M: 1, Q: 2, H: 3 };

    function versionFor(byteLen, ecc) {
        var i = ECC_INDEX[ecc] == null ? 1 : ECC_INDEX[ecc];
        for (var v = 1; v <= 20; v++) if (CAP[v][i] >= byteLen) return v;
        return null;                       // beyond what a card can carry
    }
    function modulesFor(version) { return 17 + 4 * version; }

    /* ── the verdict ────────────────────────────────────────────────────────
     * Everything above exists to produce this: given the data, the chosen
     * format and the size the QR is drawn at, will it scan off the printed
     * card?
     *
     * `quiet` is the mandatory 4-module silent border. It is part of the
     * symbol, not padding around it — a QR butted against a background colour
     * with no quiet zone fails to acquire on many readers, and it is the single
     * most common reason a "correctly generated" QR does not work.
     */
    var MIN_MODULE_MM = 0.4;      // hand-held phone camera, reliable
    var TIGHT_MODULE_MM = 0.33;   // works, but needs a steady hand and light

    /* `countModules` — when the caller has a real QR encoder loaded, it hands
     * one in and the verdict stops being an estimate. The ISO table above then
     * only covers the moment before the 20 KB encoder has arrived, so a user
     * typing on a cold cache still gets a warning rather than silence.
     *
     * The table is deliberately byte-mode-only, which makes it a conservative
     * UPPER bound: a real encoder segments mixed ASCII/Cyrillic into alphanumeric
     * and byte runs and often lands one or two versions smaller. Verified
     * 2026-08-23 against the shipped encoder — 17 of 21 payloads exact, 4
     * predicted larger, none predicted smaller. Erring large means we warn early;
     * erring small would mean promising a QR that does not scan.
     */
    function plan(payload, sizeMm, ecc, countModules) {
        ecc = ecc || 'M';
        var bytes = utf8Bytes(payload);
        var real = null;
        if (typeof countModules === 'function') {
            try { real = countModules(payload, ecc); } catch (e) { real = null; }
        }
        var version = real ? (real - 17) / 4 : versionFor(bytes, ecc);
        if (!version) {
            return { bytes: bytes, version: null, level: 'too_big', moduleMm: 0, ecc: ecc };
        }
        var modules = (real || modulesFor(version)) + 8;   // + quiet zone both sides
        var moduleMm = sizeMm / modules;
        var level = moduleMm >= MIN_MODULE_MM ? 'ok'
                  : (moduleMm >= TIGHT_MODULE_MM ? 'tight' : 'unscannable');
        return {
            bytes: bytes,
            version: version,
            modules: modules,
            measured: !!real,
            moduleMm: Math.round(moduleMm * 1000) / 1000,
            /* What the QR would have to grow to in order to be comfortable —
             * the actionable half of the warning. */
            neededMm: Math.ceil(modules * MIN_MODULE_MM * 10) / 10,
            ecc: ecc,
            level: level,
        };
    }

    /* ── the shortening ladder ──────────────────────────────────────────────
     * When the payload does not fit, drop capability in the order that costs
     * the scanner least. Each rung is a real trade the user can understand, and
     * the UI names the one it took rather than silently mangling the contact.
     *
     * Error correction is lowered LAST and never below M. It is tempting to go
     * to L first because it is free bytes, but a business card lives in a
     * wallet and gets creased exactly across the middle; H/Q is what survives
     * that, and L on a creased card is a QR that used to work.
     */
    function build(d, sizeMm, opt) {
        opt = opt || {};
        var ecc = opt.ecc || 'M';
        var count = opt.countModules;
        var rungs = [
            { id: 'full',    make: function () { return vcard3(d); } },
            { id: 'no_extras', make: function () { return vcard3(d, { short: true }); } },
            { id: 'mecard',  make: function () { return mecard(d); } },
            { id: 'mecard_min', make: function () {
                return mecard({ name: d.name, phone: d.phone, email: d.email });
            } },
        ];
        if (opt.mode === 'url' || opt.mode === 'custom') {
            var text = opt.mode === 'url' ? normUrl(opt.url) : String(opt.text || '');
            return finish(text, 'literal');
        }
        for (var i = 0; i < rungs.length; i++) {
            var p = rungs[i].make();
            var pl = plan(p, sizeMm, ecc, count);
            if (pl.level === 'ok' || i === rungs.length - 1) return finish(p, rungs[i].id, pl);
        }
        return finish(rungs[0].make(), 'full');

        function finish(text, rung, pl) {
            return {
                payload: text,
                rung: rung,
                plan: pl || plan(text, sizeMm, ecc, count),
            };
        }
    }

    return {
        esc: esc,
        telUri: telUri,
        splitName: splitName,
        vcard3: vcard3,
        mecard: mecard,
        normUrl: normUrl,
        tgUrl: tgUrl,
        waUrl: waUrl,
        utf8Bytes: utf8Bytes,
        versionFor: versionFor,
        modulesFor: modulesFor,
        plan: plan,
        build: build,
        MIN_MODULE_MM: MIN_MODULE_MM,
    };
});
